import fixWebmDuration from 'fix-webm-duration';
import { WebCodecsRecorder } from './WebCodecsRecorder';

export interface RecorderOptions {
  fps?: number;
  mimeType?: string;
  bitsPerSecond?: number;
  /**
   * Force a specific backend. Defaults to auto: prefers WebCodecs when
   * available, falls back to MediaRecorder.
   */
  backend?: 'auto' | 'webcodecs' | 'mediarecorder';
}

export type RecorderBackend = 'webcodecs' | 'mediarecorder';

/**
 * Canvas recorder facade. Picks the best backend available:
 *
 *   - `WebCodecsRecorder` produces a properly seekable WebM (Cues + duration),
 *     which fixes the long-standing "video.currentTime doesn't seek cleanly"
 *     problem that MediaRecorder's output has.
 *   - `MediaRecorder` is used as a fallback for browsers without WebCodecs
 *     (notably older Safari builds).
 *
 * Frame driving: WebCodecs backend expects `captureFrame()` to be invoked
 * once per source video frame (typically via rVFC). The MediaRecorder
 * backend is driven automatically by the canvas captureStream. Calling
 * `captureFrame()` on the MediaRecorder backend is a no-op.
 */
export class Recorder {
  private webcodecs: WebCodecsRecorder | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private stream: MediaStream | null = null;
  private startedAt = 0;
  private active: RecorderBackend | null = null;
  private lastFirstFrameWallclock: number | null = null;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly opts: RecorderOptions = {},
  ) {}

  /** Which backend would be used on the next start(). */
  plannedBackend(): RecorderBackend {
    const mode = this.opts.backend ?? 'auto';
    if (mode === 'mediarecorder') return 'mediarecorder';
    if (mode === 'webcodecs') return 'webcodecs';
    return WebCodecsRecorder.isSupported() ? 'webcodecs' : 'mediarecorder';
  }

  activeBackend(): RecorderBackend | null {
    return this.active;
  }

  isRecording(): boolean {
    if (this.active === 'webcodecs') return !!this.webcodecs?.isRecording();
    return this.mediaRecorder !== null && this.mediaRecorder.state === 'recording';
  }

  elapsedMs(): number {
    if (!this.isRecording()) return 0;
    if (this.active === 'webcodecs') return this.webcodecs?.elapsedMs() ?? 0;
    return performance.now() - this.startedAt;
  }

  /**
   * Wallclock time (`performance.now()`) corresponding to the recorded
   * video's `t = 0`. For WebCodecs this is the first encoded frame's
   * capture time (the muxer is offset-aligned). For MediaRecorder — which
   * starts capturing synchronously in `start()` — this is the recorder
   * start time. Returns null if no frame has been captured yet.
   */
  firstFrameWallclock(): number | null {
    if (this.active === 'webcodecs') {
      const live = this.webcodecs?.getFirstFrameWallclock() ?? null;
      if (live !== null) this.lastFirstFrameWallclock = live;
      return live;
    }
    if (this.active === 'mediarecorder') {
      return this.startedAt;
    }
    // Post-stop: fall back to the last known value so callers can read
    // the anchor after `await recorder.stop()`.
    return this.lastFirstFrameWallclock;
  }

  start(): void {
    if (this.isRecording()) return;
    const backend = this.plannedBackend();

    if (backend === 'webcodecs') {
      this.webcodecs = new WebCodecsRecorder(this.canvas, {
        fps: this.opts.fps,
        bitsPerSecond: this.opts.bitsPerSecond,
      });
      this.webcodecs.start();
      this.active = 'webcodecs';
      return;
    }

    // MediaRecorder fallback
    const fps = this.opts.fps ?? 30;
    this.stream = this.canvas.captureStream(fps);
    this.chunks = [];
    const mimeType = pickMimeType(this.opts.mimeType);
    this.mediaRecorder = new MediaRecorder(this.stream, {
      mimeType,
      videoBitsPerSecond: this.opts.bitsPerSecond ?? 4_000_000,
    });
    this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
      if (event.data && event.data.size > 0) this.chunks.push(event.data);
    };
    this.mediaRecorder.start(1000);
    this.startedAt = performance.now();
    this.active = 'mediarecorder';
  }

  /**
   * Push the current canvas state into the encoder. On the MediaRecorder
   * backend this is a no-op — the captureStream handles sampling.
   */
  captureFrame(): void {
    if (this.active === 'webcodecs') this.webcodecs?.captureFrame();
  }

  async stop(): Promise<Blob | null> {
    if (this.active === 'webcodecs') {
      // Grab the wallclock anchor before tearing the inner recorder down.
      this.lastFirstFrameWallclock = this.webcodecs?.getFirstFrameWallclock() ?? null;
      const blob = await (this.webcodecs?.stop() ?? Promise.resolve(null));
      this.webcodecs = null;
      this.active = null;
      return blob;
    }
    if (!this.mediaRecorder) return null;
    this.lastFirstFrameWallclock = this.startedAt;
    const durationMs = performance.now() - this.startedAt;
    const recorder = this.mediaRecorder;
    const mimeType = recorder.mimeType;
    const rawBlob = await new Promise<Blob | null>((resolve) => {
      recorder.onstop = () => {
        const b = new Blob(this.chunks, { type: mimeType });
        this.chunks = [];
        this.stream?.getTracks().forEach((t) => t.stop());
        this.stream = null;
        this.mediaRecorder = null;
        resolve(b);
      };
      recorder.stop();
    });
    this.active = null;
    // MediaRecorder's WebM is notorious for lacking a Duration field,
    // which makes `video.duration` read as `Infinity` in the viewer.
    // Patch it post-hoc.
    if (rawBlob && mimeType.includes('webm') && durationMs > 0) {
      try {
        return await fixWebmDuration(rawBlob, durationMs, { logger: false });
      } catch (err) {
        console.warn('[Recorder] fixWebmDuration failed', err);
      }
    }
    return rawBlob;
  }
}

export function pickMimeType(preferred?: string): string {
  const candidates = [
    preferred,
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
    'video/mp4',
  ].filter((v): v is string => typeof v === 'string');
  for (const c of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(c)) {
      return c;
    }
  }
  return 'video/webm';
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
